import { useCallback, useMemo, useState } from 'preact/hooks'
import { PageBackButton, PageHeader, Separator } from './AccountSectionWrapper'
import { useToggle } from '../hooks/useToggle'
import { PageModal } from './AccountModal'
import { useAccountLayoutContext } from './AccountProfileContext'
import { WishlistRelativeModal } from './WishlistRelativeModal'
import { getAllWishlists, removeProductFromAllWishlists } from '../helpers/getAllWishlists'
import { WsIcon } from '../../searchspring/components/ProductWishlist'
import { accountTranslations } from '../helpers/translations'
import { createProfileClientProxy } from './AccountWishlistPDP'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
const PRODUCT_IMAGE = 'https://cdn.shopify.com/s/files/1/0690/5538/1801/files/4-501874.jpg?v=1720590706&width=960'

/**
 *  @typedef {import("../types/shopify").ShopifyProduct} ShopifyProduct
 */

async function handleSubscription({ customerEmail, klaviyoCompanyId, unavailableStockVariant }) {
  const payload = {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: { email: customerEmail }
          }
        },
        channels: ['EMAIL']
      },
      relationships: {
        variant: {
          data: {
            type: 'catalog-variant',
            id: `$shopify:::$default:::${unavailableStockVariant} `
          }
        }
      }
    }
  }
  return fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${klaviyoCompanyId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      revision: GM_STATE.apps.klaviyo.apiRevision
    },
    body: JSON.stringify(payload)
  })
}

/**
 *
 * @param {{product: ShopifyProduct & {selected: id}, handleSelectedProduct: (input: any) => void}} props
 */
const AccountWishlistCard = (props) => {
  const { product, omneoClient, wishlist, handleSelectedProduct, clearSelected } = props
  const productTitle = product.variants[0]?.metafields?.find((m) => m?.key === 'customer_facing_product_name')?.value || product.title

  const [isLoading, setIsLoading] = useState(false)
  const variant = product.variants.find((v) => v.sku === product.selected)

  const handleRemoveFromWishlist = async (e) => {
    e.preventDefault()
    if (isLoading) return
    setIsLoading(true)
    await removeProductFromAllWishlists(omneoClient, [wishlist], product.shopifyId)
    await getAllWishlists(omneoClient)
    document.dispatchEvent(new Event('omneo:wishlist:update'))
    setIsLoading(false)
    clearSelected()
  }

  return (
    <div className={'relative grid grid-cols-1 gap-3 grid-rows-[minmax(50%,_min-content)_12px_auto]'}>
      <button type="button" className={'absolute right-4 top-4'} onClick={handleRemoveFromWishlist}>
        <WsIcon hasItem={true} />
      </button>
      <a href={`/products/${product.handle}`} alt={productTitle} className={'flex flex-col h-fit gap-3'}>
        {/* Product Image */}
        <div className={'w-full'}>
          <div className={'aspect-1 w-full'}>
            <img className={'h-full w-full object-cover object-bottom'} src={product.featuredImage.url} />
          </div>
        </div>
        {/* Product Details */}
        <div class="">
          <p className={'paragraph-sm font-bold'}>{product.vendor}</p>
          <p className={'text-neutrals-secondary-grey'}>{productTitle}</p>
        </div>
      </a>
      <Separator type="xs" />
      <div className={'flex flex-col gap-3 justify-between'}>
        {/* Product Price & Size */}
        <div className={'flex w-full flex-row justify-between flex-wrap'}>
          <div className={'paragraph-md font-bold'}>${product.priceRange.maxVariantPrice.amount}</div>
          <div className={'flex gap-2 text-neutrals-secondary-grey xl-max:flex-wrap'}>
            {variant.selectedOptions.map((o) => {
              if (o.name === 'Size') {
                const value = o.value.split('/').pop()
                return (
                  <span>
                    {o.name}: <span className={'lowercase'}>{value}</span>
                  </span>
                )
              }
              return (
                <span>
                  {o.name} :{o.value}
                </span>
              )
            })}
          </div>
        </div>
        {/* Product Actions */}
        <div className={'flex w-full flex-row justify-between gap-4 xl-max:flex-wrap'}>
          <input type="hidden" name={variant.sku} value={variant.id} />

          <div
            onClick={() => {
              handleSelectedProduct(product)
              /// open modal
            }}
            className={'btn btn-md btn-outline-black w-full'}
          >
            Change Size
          </div>
          <button
            type="button"
            disabled={!variant.availableForSale}
            onClick={async () => {
              await window.Cart.addLineItems({
                id: variant.id.split('/').pop(),
                quantity: 1
              })
            }}
            className={'btn btn-md btn-solid-black w-full'}
          >
            Add to bag
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 *
 * @param {{products: ShopifyProduct[]}} param0
 */
const AddAllAvailableItemsToCart = ({ productList }) => {
  return (
    <div className={'w-full py-2'}>
      <div
        onClick={async () => {
          const quantity = 1
          const items = productList.reduce((acc, list) => {
            const { lineItem, product } = list
            const key = lineItem.product_variant.sku

            const variant = product.variants.find(({ sku }) => sku === key)
            if (!variant) return acc
            if (!variant.availableForSale) return acc
            const id = variant.id.split('/').pop()
            acc.push({ id, quantity })
            return acc
          }, [])

          if (items.length) {
            await window.Cart.addLineItems(items)
          }
        }}
        className={'btn btn-md btn-outline-black w-full'}
      >
        Add items to bag
      </div>
    </div>
  )
}

const NotifyCustomerMessagesKlaviyo = ({ config }) => {
  if (config.messages.error) {
    return <p className={'text-system-error'}>{config.messages.error}</p>
  }
  if (config.notifiedVariants.has(config.selected.variantId)) {
    return (
      <div
        className={'bg-brand-beige pointer-events-none flex h-full w-full items-center gap-1 pt-2 transition-opacity'}
      >
        <Icon id={'tick'} class={'h-5 w-5'} />
        {config.messages.success}
      </div>
    )
  }
  return <></>
}

const AccountWishListFormButtons = ({ config, isLoading }) => {
  const isAvailable = !!config.selected.unavailableStock

  if (isAvailable) {
    return (
      <div className={'w-full'}>
        <NotifyCustomerKlaviyo config={config} />
        <button className={'btn btn-md btn-outline-black w-full justify-center'}>
          Notify me when {config.selected.unavailableStock} is back
        </button>
      </div>
    )
  }

  return (
    <button className={'btn btn-md btn-solid-black w-full justify-center relative'}>
      {isLoading && <LoaderSpin />}
      <span className={isLoading ? 'invisible' : ''}>{accountTranslations.wishlist.update_wishlist}</span>
    </button>
  )
}

const getListData = (wishlist, selectedSku) => {
  const listId = wishlist.id
  const listItemId = wishlist.items.find((item) => item.product_variant.sku === selectedSku)?.id

  return {
    id: listId,
    listItemId
  }
}

const AccountWishListForm = ({ handleRefetch, selectedProduct, modal, list, wishlistId, lineItem, client }) => {
  const [config, setConfig] = useState({
    state: '',
    notifiedVariants: new Set(),
    selected: {
      variantId: '',
      unavailableStock: ''
    },
    messages: {
      success: '',
      error: ''
    }
  })
  const [isLoading, setIsLoading] = useState(false)

  const handleConfig = (key, value) => {
    setConfig((c) => ({
      ...c,
      [key]: value
    }))
  }

  const handleOnActiveSelectionCallback = useCallback(
    (e) => {
      const data = new FormData(e.currentTarget)
      const unavailableStock = data.get('unavailable_stock') ?? ''
      const id = data.get('id')
      handleConfig('selected', { variantId: id, unavailableStock })
      console.log('config', config)
    },
    [config]
  )

  /**
   * Form submit callback to handle the klaviyo alerts / updating the wishlist item
   */
  const handleFormSubmitCallback = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    const data = new FormData(e.target)
    const variantDetails = selectedProduct.sizeData.find((size) => size.id === config.selected.variantId)

    const listItem = getListData(list, modal.selected)

    let profileToUse = client.profileClient
    if (list.profile && list.profile.id !== client.profileClient?.profile?.id?.toString()) {
      profileToUse = await createProfileClientProxy(client, list.profile.id)
    }

    await profileToUse.updateWishlistItem(listItem.id, listItem.listItemId, {
      product_variant_sku: variantDetails.sku,
      meta: {
        img: selectedProduct.modal.featuredImage.url,
        selected: variantDetails.sku,
        productId: selectedProduct.modal.id.replace('gid://shopify/Product/', ''),

        variantId: variantDetails.id.replace('gid://shopify/ProductVariant/', '')
      }
    })

    if (list.profile && list.profile.id !== client.profileClient?.profile?.id?.toString()) {
      await client.client.profile.Connection()
    }

    await getAllWishlists(client)
    handleRefetch()
    setIsLoading(false)
  }

  return (
    <div className={'flex h-full flex-col justify-between'}>
      <div className={'h-full w-full flex-grow'}>
        {!!selectedProduct.sizeData.length && (
          <form
            className={'flex h-full flex-col justify-between'}
            onClick={handleOnActiveSelectionCallback}
            onSubmit={handleFormSubmitCallback}
          >
            <x-product-selector
              class="breathe block min-h-10 w-full scroll-m-80"
              data-type="UK"
              data-width={selectedProduct.widthsList}
              data-product={modal.id}
              data-options={JSON.stringify(selectedProduct.sizeData)}
            ></x-product-selector>
            <div>
              <div className={'flex flex-row gap-4 bg-neutrals-base p-4'}>
                <AccountWishListFormButtons config={config} isLoading={isLoading} />
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export const AccountWishlist = (props) => {
  const [modal, setModal] = useState()
  const { isActive, handleToggle } = useToggle()
  const shareWishlistModal = useToggle()
  const handleSelectedProduct = (product) => {
    setModal(product)
    handleToggle()
  }
  const selectedProduct = useMemo(() => {
    if (!modal) {
      return {
        sizeData: [],
        widthsList: undefined,
        modal: null
      }
    }

    const widthData = new Set(
      modal.variants
        .map((v) => {
          const width = v.selectedOptions.find((o) => o.name === 'Width')
          // eslint-disable-next-line array-callback-return
          if (!width) return
          return width.value
        })
        .filter((v) => v)
    )
    const widthsList = Array.from(widthData.values()).join(',')

    const d = modal.variants.reduce((acc, v) => {
      const attributes = v.metafields.reduce((bcc, m) => {
        if (!m) return bcc
        return {
          ...bcc,
          [m.key]: m.value
        }
      }, {})
      const productDataWithoutWidths = {
        id: v.id.replace('gid://shopify/ProductVariant/', ''),
        name: v.title,
        sku: v.sku,
        age_group: attributes.age_group,
        available: v.availableForSale,
        uk: attributes?.kids_uk,
        us: attributes?.kids_us,
        eu: attributes?.euro,
        cm: attributes?.centimeter
      }
      const width = v.selectedOptions.find((o) => o.name === 'Width')
      if (width) {
        const productDataWithWidths = {
          ...productDataWithoutWidths,
          width: width.value
        }
        acc.push(productDataWithWidths)
        return acc
      }

      acc.push(productDataWithoutWidths)
      return acc
    }, [])

    const w = widthsList.length === 1 ? widthsList.concat(',') : widthsList
    return {
      sizeData: d,
      widthsList: w,
      modal
    }
  }, [modal])

  const productList = useMemo(() => {
    return props?.wishlist?.items?.map((item) => {
      const id = item.meta.productId
      const productData = props.allProducts[id]
      const selected = item?.meta?.selected
      if (!productData || !selected) return {}
      return {
        product: {
          ...productData,
          selected
        },
        wishlistId: props.wishlist.id,
        lineItem: item
      }
    })
  }, [])

  return (
    <>
      <PageModal
        props={{
          isActive,
          handleToggle,
          modalTitle: 'Edit Item',
          modal: (
            <AccountWishListForm
              handleRefetch={props.handleRefetch}
              list={props.wishlist}
              client={props.omneoClient}
              modal={modal}
              selectedProduct={selectedProduct}
            />
          )
        }}
      >
        <form className={'flex flex-col gap-4'}>
          <PageBackButton state="wishlist" />
          <div className={'flex w-full flex-row justify-between'}>
            <PageHeader
              title={props.wishlist?.name ?? props.wishlist?.title}
              icon={
                props.wishlist?.profile?.connector?.avatar_url ? (
                  <img className={'h-10 w-10 rounded-full'} src={props.wishlist?.profile?.connector?.avatar_url} />
                ) : null
              }
            />
            {!props.wishlist?.profile && (
              <div className={'flex flex-row gap-2'}>
                <WishlistRelativeModal
                  title={'Edit wishlist'}
                  buttonTitle={'Edit'}
                  handleRefetch={props.handleRefetch}
                  wishlist={{
                    id: props.wishlist?.id,
                    name: props.wishlist?.name ?? props.wishlist?.title
                  }}
                />
              </div>
            )}
          </div>
          {props?.wishlist?.items?.length ? (
            <>
              <AddAllAvailableItemsToCart productList={productList} />
              <div className={'grid grid-cols-2 gap-4 3xl:grid-cols-3'}>
                {productList.map((item) => {
                  if (!item?.wishlistId) return <></>
                  return (
                    <AccountWishlistCard
                      product={item.product}
                      wishlistId={item.item}
                      lineItem={item.lineItem}
                      handleSelectedProduct={handleSelectedProduct}
                      omneoClient={props.omneoClient}
                      wishlist={props.wishlist}
                      clearSelected={props.clearSelected}
                    />
                  )
                })}

                {/* {props.wishlist.items.map((item) => {
                  const id = item.product.external_id
                  const productData = props.allProducts[id]
                  const selected = item?.meta?.selected
                  if (!productData || !selected) return <></>
                  return (
                    <AccountWishlistCard
                      product={{
                        ...productData,
                        selected
                      }}
                      wishlistId={props.wishlist.id}
                      lineItem={item}
                      handleSelectedProduct={handleSelectedProduct}
                    />
                  )
                })} */}
              </div>
            </>
          ) : (
            <div>
              <div>{accountTranslations.wishlist.no_items}</div>
            </div>
          )}
        </form>
      </PageModal>
    </>
  )
}
